import { FC, useEffect, useMemo, useState } from 'react'
import { useAsyncFn } from 'react-use'
import mclasses from '../../../../styles/modal.module.css'
import Modal from '@common/Modal'
import { crmLeadParentApi } from '@/api'
import Select from '../../../base/Select/Select'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import { useAddAlert } from '@/hooks/useAddAlert'
import { isError } from '@/helpers/checkQuery'
import type { LeadProfileLead } from '../../LeadProfileModal.types'
import type {
  CrmLeadParent,
  CrmLeadParentCandidate,
} from '@/api/types/crm/crmLeadParent.types'
import { getLeadParentApiErrorMessage } from './leadParentApiError'
import classes from './index.module.css'

const ADD_PARENT_PLACEHOLDER =
  'Введите ФИО, почту или номер телефона родителя'

interface AddParentAccountModalProps {
  lead?: LeadProfileLead
  open: boolean
  onClose: () => void
  onSuccess?: (parents: CrmLeadParent[]) => void
}

const AddParentAccountModal: FC<AddParentAccountModalProps> = ({
  lead,
  open,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const [searchText, setSearchText] = useState('')
  const [selectParentId, setSelectParentId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const studentId =
    lead?.user_id != null && Number.isFinite(Number(lead.user_id))
      ? Number(lead.user_id)
      : null

  const boundParentIds = useMemo(() => {
    const parents = lead?.parents
    const list = Array.isArray(parents) ? parents : []
    return new Set(
      list
        .map((item) => Number(item?.id))
        .filter((id) => Number.isFinite(id) && id > 0),
    )
  }, [lead?.parents])

  const [candidatesState, doFetchCandidates] = useAsyncFn(async () => {
    if (!lead?.id) {
      return [] as CrmLeadParentCandidate[]
    }

    const res = await crmLeadParentApi.getCandidates({
      lead_id: lead.id,
      search: searchText.trim() || undefined,
      page: 1,
      per_page: 50,
    })
    const items = res?.data?.data?.items
    return Array.isArray(items) ? (items as CrmLeadParentCandidate[]) : []
  }, [lead?.id, searchText])

  useEffect(() => {
    if (open) {
      setSearchText('')
      setSelectParentId(null)
      setSubmitting(false)
    }
  }, [open])

  useEffect(() => {
    if (!open || !lead?.id) return
    void doFetchCandidates()
  }, [open, lead?.id, searchText, doFetchCandidates])

  const options = useMemo(() => {
    const items = candidatesState.value ?? []
    return items
      .filter((item) => studentId == null || item.id !== studentId)
      .map((item) => ({
        value: item.id,
        label: item.label || item.name,
        disabled: item.is_direct === true || boundParentIds.has(item.id),
      }))
  }, [candidatesState.value, studentId, boundParentIds])

  const handleConfirm = async () => {
    if (!selectParentId) {
      addAlert({
        title: 'Ошибка',
        text: 'Не выбран личный кабинет родителя',
        type: 'error',
      })
      return
    }

    if (studentId != null && selectParentId === studentId) {
      addAlert({
        title: 'Ошибка',
        text: 'Нельзя привязать ученика лида как родителя',
        type: 'error',
      })
      return
    }

    if (!lead?.id) {
      addAlert({
        title: 'Ошибка',
        text: 'Лид не найден',
        type: 'error',
      })
      return
    }

    setSubmitting(true)
    try {
      const res = await crmLeadParentApi.attach({
        lead_id: lead.id,
        parent_id: selectParentId,
      })
      if (isError(res)) {
        throw Object.assign(new Error('attach failed'), { response: res })
      }

      const data = res.data.data
      addAlert({
        title: 'Успешно',
        text: data?.message || 'Родитель привязан к лиду.',
        type: 'success',
      })
      onSuccess?.(Array.isArray(data?.parents) ? data.parents : [])
    } catch (error) {
      addAlert({
        title: 'Ошибка',
        text: getLeadParentApiErrorMessage(
          error,
          'Ошибка привязки личного кабинета родителя',
        ),
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      active={open}
      className={mclasses.modalWrapper as any}
      setActive={() => onClose()}
    >
      <div className={mclasses.modalContainer}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>
              Привязать ЛК родителя
            </span>
          </div>
          <div className={mclasses.closeIcon} onClick={() => onClose()}>
            <IcoRemove />
          </div>
        </div>
        <div className={mclasses.formWrapper}>
          <div>
            <p className={mclasses.label}>Выбрать личный кабинет родителя</p>
            <Select
              showSearch
              loading={candidatesState.loading}
              options={options}
              filterOption={false}
              onSearch={(text) => setSearchText(text)}
              value={selectParentId}
              onChange={(value: number | null) => setSelectParentId(value)}
              placeholder={ADD_PARENT_PLACEHOLDER}
              notFoundText="Родители не найдены"
            />
            {studentId != null ? (
              <p className={classes.studentLinkHint}>
                У лида привязан ЛК ученика: родитель будет привязан и к ученику.
              </p>
            ) : null}
          </div>
        </div>
        <div className={mclasses.actions}>
          <Button color="gray" onClick={() => onClose()}>
            Отменить
          </Button>
          <Button
            color="primary"
            onClick={() => void handleConfirm()}
            isLoading={submitting}
            disabled={!selectParentId || submitting}
          >
            Подтвердить
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default AddParentAccountModal
